<template>
	<div
		:class="[groupStyle]"
		class="menu-options-group"
		v-on="$listeners"
	>
		<h2
			v-if="title"
			:class="[{ bold: bold }, groupStyle]"
			class="menu-options-group-title"
		>
			{{ title }}
		</h2>

		<slot name="items-before" />

		<div
			v-for="(item, index) in items"
			:key="index"
			:class="{ focused: isFocused(item) }"
		>
			<slot
				:item="item"
				:isSelected="isSelected(item)"
				:isFocused="isFocused(item)"
			/>
		</div>
		<slot name="items-after" />
	</div>
</template>

<script>
export default {
	name: 'TMenuOptionsGroup',
	props: {
		title: String,
		items: Array,
		selectedItem: [ String, Object, Number, Boolean ],
		selectedItems: Array,
		groupStyle: String,
		bold: Boolean,
		autoSelectFirst: {
			type: Boolean,
			default: true
		},
		disableSpaceToSelect: Boolean,
		enableFocusNextOnKeyboardSelect: Boolean,
	},
	data() {
		return { focusedIndex: -1 };
	},
	watch: {
		items: {
			immediate: true,
			handler(items) {
				if (
					this.autoSelectFirst
					&& items.length === 1
				)
					this.setFocusFirst();

				else this.focusedIndex = -1;
			}
		}
	},
	provide() {
		return { setSelected: this.setSelected, };
	},
	created() {
		this.$nextTick(
			() => document.addEventListener('keydown', this.onKeyDown, { capture: true })
		);
	},
	destroyed() {
		document.removeEventListener('keydown', this.onKeyDown, { capture: true });
	},
	methods: {
		setSelected(value, event) {

			this.$emit('update:selected', value);

			if (
				this.selectedItems
				&& this.selectedItems.some( item =>
					item
					&& value
					&& item === value
				)
			) {
				this.$emit('change', null);

			} else {
				this.$emit('change', value);
			}

			if (
				this.enableFocusNextOnKeyboardSelect
				&& event instanceof KeyboardEvent
			)
				window.focusNext();
		},
		isSelected(item) {
			if (
				this.selectedItems
				&& this.selectedItems.length > 0
			) {
				return this.selectedItems.indexOf(item) > -1
					|| Boolean( this.selectedItems.find( x => x === item ))
					|| Boolean(
						item.id
							? this.selectedItems.find(x => x.id === item.id)
							: false
					);

			} else if (item) {
				if (item.id) {
					return Boolean(this.selectedItem) && this.selectedItem.id === item.id;
				}
				return Boolean(this.selectedItem) && this.selectedItem === item;
			}
			return this.selectedItem !== undefined && this.selectedItem === item;
		},
		isFocused(item) {
			return this.items.indexOf(item) === this.focusedIndex;
		},
		scrollIntoView() {
			let focusedChild = this.$children[this.focusedIndex];
			if (focusedChild) {
				focusedChild.$el.scrollIntoView();
			}
		},
		decrementFocus(event, count = 1) {
			this.setFocusedIndex(this.focusedIndex - count);
			event.preventDefault();
			event.stopPropagation();
		},
		incrementFocus(event, count = 1) {
			this.setFocusedIndex(this.focusedIndex + count);
			event.preventDefault();
			event.stopPropagation();
		},
		setFocusFirst() {
			this.setFocusedIndex(0);
		},
		setFocusLast() {
			this.setFocusedIndex(this.items.length - 1);
		},
		setFocusedIndex(focusedIndex) {
			if (focusedIndex > this.items.length - 1)
				this.focusedIndex = 0;

			else if (focusedIndex < 0)
				this.focusedIndex = this.items.length - 1;

			else
				this.focusedIndex = focusedIndex;

			this.$emit('update:focusedIndex', this.focusedIndex);

			this.scrollIntoView();
		},
		setFocusedItemSelected(event) {
			if (this.focusedIndex === -1)
				return;

			let item = this.items[this.focusedIndex];

			this.setSelected(item, event);

			event.preventDefault();
			event.stopPropagation();
		},
		setFocusedItemSelectedWithTab(event) {
			if (this.focusedIndex === -1)
				return;

			let item = this.items[this.focusedIndex];

			this.setSelected(item, event);
		},
		onSpace(event) {
			if (this.disableSpaceToSelect)
				return;

			this.setFocusedItemSelected(event);
		},
		onKeyDown(event) {
			if (event.key === 'Home') this.setFocusFirst();
			if (event.key === 'End') this.setFocusLast();
			if (event.key === 'ArrowDown') this.incrementFocus(event);
			if (event.key === 'PageDown') this.incrementFocus(event, 5);
			if (event.key === 'ArrowUp') this.decrementFocus(event);
			if (event.key === 'PageUp') this.decrementFocus(event, 5);
			if (event.key === ' ' || event.key === 'Spacebar') this.onSpace(event);
			if (event.key === 'Enter') this.setFocusedItemSelected(event);
			if (event.key === 'Tab') this.setFocusedItemSelectedWithTab(event);
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/text';
@import '~@/components/evergreen/scss/colors';

.menu-options-group {
	box-sizing: border-box;

	padding-top: 8px;
	padding-bottom: 8px;

	&.combobox {
		padding-top: 0;
		padding-bottom: 0;
	}
	.focused {
		background-color: #dbeaf7;
	}
}
.menu-options-group-title {
	@extend %font-families-ui;

	user-select: none;

	letter-spacing: 0.6px;
	font-size: 11px;
	font-weight: 400;
	line-height: 16px;
	color: get-color($text, 'muted');
	text-transform: uppercase;

	margin: 8px 16px 8px 44px;

	&.combobox {
		margin: 0;
		padding: 8px;

		border-bottom: 1px solid rgb(237, 240, 242);
	}
	&.bold {
		font-weight: 600;
	}
}
</style>
